import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchFactorDatasets } from '../../../utils/ApiCall';

/** Discovery never adopts a dataset. Only the approved Watchlist workflow can do that. */
export default function useFactorDatasetCatalogue({ userUid, watchlistVersion }) {
    const [data, setData] = useState(null);
    const [capability, setCapability] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const dataRef = useRef(null);
    const requestRef = useRef(0);
    const scopeRef = useRef(userUid);
    scopeRef.current = userUid;
    const inFlightRef = useRef(null);

    const read = useCallback(async (beforeDate = null) => {
        if (!userUid || inFlightRef.current) return;
        const requestId = ++requestRef.current;
        inFlightRef.current = requestId;
        setLoading(true);
        try {
            const next = await fetchFactorDatasets({ beforeDate });
            if (requestId !== requestRef.current || scopeRef.current !== userUid) return;
            if (!next || !Array.isArray(next.items)) throw new Error('Invalid factor dataset catalogue.');
            const previous = dataRef.current;
            // Never combine pages from different published contexts.
            const append = beforeDate && previous?.current_revision_id === next.current_revision_id
                && previous?.current?.vendor === next.current?.vendor
                && previous?.current?.model === next.current?.model
                && previous?.current?.horizon === next.current?.horizon;
            const items = append
                ? [...new Map([...(previous.items || []), ...next.items].map(item => [item.release_id, item])).values()]
                : next.items;
            dataRef.current = { ...next, items };
            setData(dataRef.current);
            setCapability({ userUid, enabled: next.enabled === true });
            setError('');
        } catch (caught) {
            if (requestId !== requestRef.current || scopeRef.current !== userUid) return;
            setError('Available factor dates could not be checked. Try again.');
        } finally {
            if (requestId === requestRef.current) {
                inFlightRef.current = null;
                setLoading(false);
            }
        }
    }, [userUid]);

    useEffect(() => {
        requestRef.current += 1;
        inFlightRef.current = null;
        dataRef.current = null;
        setData(null);
        setError('');
        setLoading(false);
        void read();
        return () => { requestRef.current += 1; inFlightRef.current = null; };
    }, [read, watchlistVersion]);

    useEffect(() => {
        if (!userUid) return undefined;
        const refresh = () => {
            if (document.visibilityState === 'visible' && dataRef.current?.enabled !== false) void read();
        };
        const timer = window.setInterval(refresh, 60000);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [read, userUid]);

    return {
        data, loading, error,
        // Revision refreshes discard date choices, not the known user capability.
        enabled: capability?.userUid === userUid && capability.enabled === true,
        refresh: () => read(),
        loadMore: () => dataRef.current?.next_before_date ? read(dataRef.current.next_before_date) : undefined,
    };
}
